/* harmony-header — nav dropdown behaviour + sticky header.
   [data-h-nav-item] is keyed via data-h-nav-key to its panel
   ([data-h-mega-panel-for] / [data-h-dropdown-panel-for]) — panels live on
   <header> itself because the frosted pill's backdrop-filter traps absolute
   positioning. `data-open` is mirrored on both the item and the panel. Hover
   on desktop, first-tap opens on touch, focus-within keeps it open for
   keyboard, Escape / leaving / another item opening closes it. Sticky modes:
   'always' | 'on-scroll-up' | 'none' via data-sticky-mode. */
(function () {
  'use strict';

  var header = document.querySelector('[data-h-header]');
  if (!header) return;

  var items = Array.prototype.slice.call(header.querySelectorAll('[data-h-nav-item]'));
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');

  function panelFor(item) {
    var key = item.dataset.hNavKey;
    if (key == null) return null;
    return header.querySelector(
      '[data-h-mega-panel-for="' + key + '"], [data-h-dropdown-panel-for="' + key + '"]'
    );
  }

  function setPanelOpen(item, open) {
    var panel = panelFor(item);
    if (!panel) return;
    if (open) panel.dataset.open = ''; else delete panel.dataset.open;
  }

  function closeAll(except) {
    items.forEach(function (item) {
      if (item === except) return;
      delete item.dataset.open;
      setPanelOpen(item, false);
      var link = item.querySelector('[data-h-nav-link]');
      if (link) link.setAttribute('aria-expanded', 'false');
    });
  }

  function setOpen(item, open) {
    if (open) closeAll(item);
    if (open) item.dataset.open = ''; else delete item.dataset.open;
    setPanelOpen(item, open);
    var link = item.querySelector('[data-h-nav-link]');
    if (link) link.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  items.forEach(function (item) {
    var link = item.querySelector('[data-h-nav-link]');
    var panel = panelFor(item);
    if (!link || !panel) return;

    var hideTimer = null;
    var open = function () { clearTimeout(hideTimer); setOpen(item, true); };
    var scheduleClose = function () {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () { setOpen(item, false); }, 180);
    };

    if (fine.matches) {
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', scheduleClose);
      panel.addEventListener('mouseenter', function () { clearTimeout(hideTimer); });
      panel.addEventListener('mouseleave', scheduleClose);
    }

    // Touch / hybrid: first tap opens, second tap navigates.
    link.addEventListener('click', function (e) {
      if (!fine.matches && !item.dataset.open) {
        e.preventDefault();
        setOpen(item, true);
      }
    });

    item.addEventListener('focusin', open);
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget) && !panel.contains(e.relatedTarget)) scheduleClose();
    });
    panel.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget) && !panel.contains(e.relatedTarget)) scheduleClose();
    });
    panel.addEventListener('focusin', function () { clearTimeout(hideTimer); });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-h-nav-item]') && !e.target.closest('[data-h-mega-panel], [data-h-dropdown-panel]')) closeAll();
  });

  // Homepage hero overlay: a `[data-h-hero]` first section pulls up under the
  // pill row (`--h-header-offset`) and the pills switch to the dark-glass
  // variant while the hero is behind them (`data-h-overlay` on the header).
  var hero = document.querySelector('[data-h-hero]');
  if (hero) {
    var setHeroOffset = function () {
      document.documentElement.style.setProperty('--h-header-offset', header.offsetHeight + 'px');
    };
    var overlayDesktop = window.matchMedia('(min-width: 768px)');
    var syncOverlay = function () {
      var mobileOnly = hero.getAttribute('data-h-hero-overlay') === 'mobile';
      var over =
        hero.getBoundingClientRect().bottom > header.getBoundingClientRect().bottom &&
        !(mobileOnly && overlayDesktop.matches);
      if (over) header.setAttribute('data-h-overlay', ''); else header.removeAttribute('data-h-overlay');
    };
    setHeroOffset();
    syncOverlay();
    window.addEventListener('resize', setHeroOffset, { passive: true });
    window.addEventListener('resize', syncOverlay, { passive: true });
    window.addEventListener('load', setHeroOffset);
    document.addEventListener('scroll', syncOverlay, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(setHeroOffset).observe(header);
  }

  // Sticky hide/show — vendored headroom.js (MIT) toggles headroom--pinned /
  // headroom--unpinned on the header. `--h-header-height` keeps stacked
  // stickies (PDP anchor nav) glued below the pills; `h-header-unpinned` on
  // <html> is the shared "header is slid away" signal.
  var mode = header.dataset.stickyMode;
  var anchorNav = document.querySelector('harmony-anchor-nav');
  var setHeaderHeight = function () {
    var headerH = mode === 'none' ? 0 : header.offsetHeight;
    var navH = anchorNav ? anchorNav.offsetHeight : 0;
    document.documentElement.style.setProperty('--h-header-height', headerH + 'px');
    document.documentElement.style.setProperty('--h-anchor-nav-height', navH + 'px');
    document.documentElement.style.setProperty('--h-header-stack', (headerH + navH) + 'px');
  };
  if (mode === 'none') {
    header.classList.remove('sticky');
    setHeaderHeight();
  } else {
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight, { passive: true });
    /* Re-measure whenever either box changes (webfont swap, late logo image,
       section re-render): a stale var parks the nav stack away from the pills. */
    if (window.ResizeObserver) {
      var stackObserver = new ResizeObserver(setHeaderHeight);
      stackObserver.observe(header);
      if (anchorNav) stackObserver.observe(anchorNav);
    }
    window.addEventListener('load', setHeaderHeight);
    if (mode === 'on-scroll-up' && typeof window.Headroom === 'function') {
      var headroom = new window.Headroom(header, {
        offset: header.offsetHeight,
        tolerance: 5,
        onUnpin: function () { document.documentElement.classList.add('h-header-unpinned'); },
        onPin: function () { document.documentElement.classList.remove('h-header-unpinned'); },
        onTop: function () { document.documentElement.classList.remove('h-header-unpinned'); }
      });
      headroom.init();
    }
  }
})();
